"use client";

import { CircleCheck, Download } from "lucide-react";

import { Button } from "@/src/components/shadcn/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/src/components/shadcn/ui/tooltip";

interface FileDownloadButtonProps {
  disabledReason?: string;
  filename?: string;
  hasDownloaded?: boolean;
  href?: string;
  onClick?: () => void | Promise<void>;
}

function DownloadIcon({ hasDownloaded }: { hasDownloaded?: boolean }) {
  return hasDownloaded ? <CircleCheck size={16} className="text-success" /> : <Download size={16} />;
}

export function FileDownloadButton({
  disabledReason,
  filename,
  hasDownloaded,
  href,
  onClick,
}: FileDownloadButtonProps) {
  if (!disabledReason && href) {
    return (
      <Button size="icon-sm" variant="ghost" asChild>
        <a href={href} download={filename} aria-label="Download file">
          <DownloadIcon hasDownloaded={hasDownloaded} />
        </a>
      </Button>
    );
  }

  const button = (
    <Button
      type="button"
      size="icon-sm"
      variant="ghost"
      className="text-muted-foreground"
      disabled={Boolean(disabledReason)}
      onClick={onClick}
      aria-label={disabledReason ? `Download unavailable: ${disabledReason}` : "Download file"}
    >
      <DownloadIcon hasDownloaded={hasDownloaded} />
    </Button>
  );

  if (!disabledReason) {
    return button;
  }

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex cursor-not-allowed" tabIndex={0}>
            {button}
          </span>
        </TooltipTrigger>
        <TooltipContent>{disabledReason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
